import { useState, useCallback } from 'react'
import type { ToastMessage, ToastType } from '@components/ui/Toast'

/**
 * Toast Management Hook
 *
 * Provides a simple API for showing and managing toast notifications.
 *
 * Features:
 * - Add toast with automatic ID generation
 * - Remove toast by ID
 * - Auto-cleanup of old toasts
 * - Type-safe toast types
 *
 * @example
 * ```tsx
 * const { toasts, showToast, removeToast } = useToast()
 *
 * // Show a success toast
 * showToast('Brick deleted successfully', 'success')
 *
 * // Show an info toast with custom duration
 * showToast('Rotation coming soon', 'info', 5000)
 * ```
 */
export const useToast = () => {
  const [toasts, setToasts] = useState<ToastMessage[]>([])

  /**
   * Show a new toast notification
   */
  const showToast = useCallback(
    (message: string, type: ToastType = 'info', duration?: number) => {
      const id = `toast-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`

      const newToast: ToastMessage = {
        id,
        message,
        type,
        duration,
      }

      setToasts((prev) => [...prev, newToast])
    },
    []
  )

  /**
   * Remove a toast by ID
   */
  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id))
  }, [])

  /**
   * Clear all toasts
   */
  const clearToasts = useCallback(() => {
    setToasts([])
  }, [])

  return {
    toasts,
    showToast,
    removeToast,
    clearToasts,
  }
}
